import {
  Button,
  Icons,
  Input,
  Modal,
  Space,
  Table,
  TableColumnType,
  TextField,
  Tooltip,
} from '@pankod/refine-antd'
import React, { useRef, useState } from 'react'
import Highlighter from 'react-highlight-words'
import { FilterConfirmProps } from 'antd/lib/table/interface'
import { TableProps } from 'antd/es/table'

import { Candidate } from '@resources/pendaftaran-diklat/types'

import type { InputRef } from 'antd'

type DataIndex = keyof Candidate

type ModalParticipantCandidateNPSNProps = {
  visible: boolean
  onCancel: () => void
  onOk: (candidates: Candidate[]) => void
  tableProps: TableProps<Candidate>
}
const ModalParticipantCandidateNPSN = ({
  visible,
  onCancel,
  onOk,
  tableProps,
}: ModalParticipantCandidateNPSNProps) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef<InputRef>(null)

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex,
  ) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText('')
  }

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): TableColumnType<Candidate> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Cari ${dataIndex}..`}
          value={`${selectedKeys[0] || ''}`}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<Icons.SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Cari
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters)
              confirm({ closeDropdown: true })
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <Icons.SearchOutlined
        style={{ color: filtered ? '#1890ff' : undefined }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text || <TextField value="Kosong" type="secondary" italic />
      ),
  })

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const columns: TableColumnType<Candidate>[] = [
    {
      title: 'Nama',
      dataIndex: 'nama',
      key: 'nama',
      ...getColumnSearchProps('nama'),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ...getColumnSearchProps('email'),
    },
    {
      title: 'NUPTK',
      dataIndex: 'nuptk',
      key: 'nuptk',
      ...getColumnSearchProps('nuptk'),
    },
    {
      title: 'No Handphone',
      dataIndex: 'phone',
      key: 'phone',
      render: (value) =>
        value || <TextField value="Kosong" type="secondary" italic />,
    },
  ]

  const rowSelection: TableProps<Candidate>['rowSelection'] = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps(record) {
      return { disabled: record.registered }
    },
    renderCell(_, record, __, originNode) {
      if (record.registered) {
        return (
          <Tooltip
            title="Peserta telah terdaftar di program ini"
            placement="topLeft"
            arrowPointAtCenter
          >
            {originNode}
          </Tooltip>
        )
      }
      return originNode
    },
  }
  const hasSelected = selectedRowKeys.length > 0

  const handleOnOk = () => {
    const candidates =
      tableProps.dataSource?.filter((candidate) =>
        selectedRowKeys.includes(candidate.key),
      ) || []
    onOk(candidates)
    onCancel()
  }
  return (
    <Modal
      title="Daftar Kandidat Peserta Didik berdasarkan NPSN Anda"
      visible={visible}
      onCancel={onCancel}
      onOk={handleOnOk}
      okButtonProps={{
        disabled: !hasSelected,
        icon: <Icons.PlusOutlined />,
      }}
      okText="Tambahkan Kandidat"
      cancelText="Batal"
      maskClosable={false}
      width={900}
      centered
      afterClose={() => {
        setSelectedRowKeys([])
        setSearchText('')
        setSearchedColumn('')
      }}
      destroyOnClose
    >
      <Table
        {...tableProps}
        rowSelection={rowSelection}
        columns={columns}
        pagination={false}
        scroll={{ y: 400 }}
      />
    </Modal>
  )
}

export default ModalParticipantCandidateNPSN
